import { useGlobalQueries } from '@/hooks/use-global-queries';
import { useLocalization } from '@/hooks/use-localization';
import { useEffect, useRef } from 'react';

declare global {
    interface Window {
        Trustpilot?: { loadFromElement: (el: HTMLElement | null, forceReload?: boolean) => void };
    }
}

export type TrustpilotWidgetProps = {
    darkMode?: boolean;
};

export const TrustpilotWidget = ({ darkMode = false }: TrustpilotWidgetProps) => {
    const { getSetting } = useGlobalQueries();
    const ref = useRef<HTMLDivElement>(null);
    const { getLocale, localize, hasTranslation } = useLocalization();
    const trustpilotSettings = getSetting('trustpilot_settings');

    useEffect(() => {
        window.Trustpilot?.loadFromElement(ref.current, true);
    }, []);

    if (!trustpilotSettings?.trustpilot_enabled) {
        return null;
    }

    const getTransformedLocale = () => {
        if (trustpilotSettings.trustpilot_locale) {
            return trustpilotSettings.trustpilot_locale;
        }

        const locale = getLocale();
        const localeArr = locale.split('-');
        return `${localeArr[0]}-${localeArr[1]?.toLocaleUpperCase()}`;
    };

    return (
        <>
            <div
                ref={ref}
                className="trustpilot-widget r-peer"
                data-locale={getTransformedLocale()}
                data-template-id="5406e65db0d04a09e042d5fc"
                data-businessunit-id={trustpilotSettings.trustpilot_businessunit_id}
                data-style-height="28px"
                data-theme={darkMode ? 'dark' : 'light'}
                data-style-width="100%"
                suppressHydrationWarning
                dangerouslySetInnerHTML={{ __html: '' }}
            />
            {/* Only rendered once the market has an actual translation for the link text — otherwise
                `localize` would surface its "No translation for …" sentinel as the visible label. */}
            {hasTranslation('trustpilot-read-reviews') && (
                <div className="r-flex r-justify-center peer-has-[iframe]:r-hidden">
                    <a
                        className="trustpilot-fallback external-link r-animate-[trustpilot-fallback-in_0s_2s_forwards] r-opacity-0"
                        href={trustpilotSettings.trustpilot_review_url}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {localize('trustpilot-read-reviews')}
                        <span className="r-sr-only">
                            {localize('external-link')} {localize('opens-in-new-tab')}
                        </span>
                    </a>
                </div>
            )}
        </>
    );
};
